import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  CLIENT_FORM_STEP_NAME,
  DEBTOR_FORM_STEP_NAME,
  FormService,
  FormStepName,
  PAYMENT_FORM_STEP_NAME,
} from 'src/app/services/form/form.service';
import payeeIconConfig from 'src/app/shared/payee-icon/payee-icon-config';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';
import { MatButton } from '@angular/material/button';
import { PayeeLanguageSelectorComponent } from '../../../shared/payee-language-selector/payee-language-selector.component';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-form-header',
  templateUrl: './form-header.component.html',
  styleUrls: ['./form-header.component.scss'],
  imports: [
    RouterLink,
    PayeeIconComponent,
    MatButton,
    PayeeLanguageSelectorComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class FormHeaderComponent implements OnInit, OnDestroy {
  private activatedRoute = inject(ActivatedRoute);
  private formService = inject(FormService);
  private translate = inject(TranslateService);
  private readonly destroy = new Subject<void>();

  @Input() hideStepper = false;
  selectedStepIndex = 0;

  get showPartnerLogo(): boolean {
    return !!this.formService.case;
  }

  get partnerLogoUrl(): string | null {
    return this.formService.case?.partner_logo_url ?? null;
  }

  get partnerLogoAlt(): string {
    return this.formService.case?.partner_id || 'Payee';
  }

  get partnerLogoName(): string | undefined {
    if (this.partnerLogoUrl) {
      return undefined;
    }
    const logo = (this.formService.case?.partner_id || 'payee') + '-logo';
    if (logo in payeeIconConfig) {
      return logo;
    }
    return 'payee-logo';
  }

  get payee_case_reference_id() {
    return this.formService.case?.payee_case_reference_id;
  }

  steps: FormStepName[] = [
    DEBTOR_FORM_STEP_NAME,
    CLIENT_FORM_STEP_NAME,
    PAYMENT_FORM_STEP_NAME,
  ];

  readonly stepNames: Record<FormStepName, Observable<unknown>> = {
    debtor: this.translate.stream('CASE_FORM.HEADER.STEPS.DEBTOR'),
    client: this.translate.stream('CASE_FORM.HEADER.STEPS.CLIENT'),
    payment: this.translate.stream('CASE_FORM.HEADER.STEPS.PAYMENT'),
    'payment-redirect': this.translate.stream('CASE_FORM.HEADER.STEPS.PAYMENT'),
  };

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.destroy)).subscribe({
      next: (params) => {
        const step = params.step as FormStepName;
        if (this.steps.includes(step)) {
          this.selectedStepIndex = this.steps.indexOf(step);
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  async navigateToStep(index: number) {
    if (index < this.selectedStepIndex) {
      switch (index) {
        case 0: {
          await this.formService.redirectToStep(DEBTOR_FORM_STEP_NAME);
          this.selectedStepIndex = index;
          break;
        }
        case 1: {
          await this.formService.redirectToStep(CLIENT_FORM_STEP_NAME);
          this.selectedStepIndex = index;
          break;
        }
      }
    }
  }

  onExitButtonClick() {
    this.formService.redirectToCases();
  }
}
